import { openDB } from 'idb';

const DB_NAME = 'makeshift-offline-cache';
const DB_VERSION = 2;
const STORE_RESPONSES = 'responses';

// Matches YYYY-MM-DD dates in a cache-key URL so we can skip caching
// requests that fall outside the viewable window.  Mirrors the monorepo's
// offlineCache.ts ISO_DATE_RE pattern.
const ISO_DATE_RE = /\d{4}-\d{2}-\d{2}/g;

let _dbPromise = null;

function getDB() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore(STORE_RESPONSES, { keyPath: 'url' });
        }
        // v1 → v2: drop the separate meta store that tracked _lastCachedAt.
        if (oldVersion === 1 && db.objectStoreNames.contains('meta')) {
          db.deleteObjectStore('meta');
        }
      },
    });
  }
  return _dbPromise;
}

function isWithinCacheWindow(url) {
  const dates = url.match(ISO_DATE_RE);
  // No date params in URL → reference/lookup data, always cache.
  if (!dates) return true;
  const windowStart = moment().startOf('month').subtract(1, 'months');
  const windowEnd = moment().endOf('month').add(1, 'months');
  return dates.some(d => {
    const dt = moment(d, 'YYYY-MM-DD');
    return dt.isSameOrAfter(windowStart) && dt.isBefore(windowEnd);
  });
}

const OfflineCache = {
  async get(url) {
    try {
      const db = await getDB();
      const record = await db.get(STORE_RESPONSES, url);
      return record ? record.data : null;
    } catch (e) {
      return null;
    }
  },

  async set(url, data) {
    if (!isWithinCacheWindow(url)) return;
    try {
      const db = await getDB();
      await db.put(STORE_RESPONSES, { url, data, cachedAt: Date.now() });
    } catch (e) {
      // Silently swallow — cache is best-effort
    }
  },

  // Returns the most recent cachedAt timestamp across all entries, or null.
  // Mirrors monorepo's getLatestCachedAt() which also scans all entries.
  async getLatestCachedAt() {
    try {
      const db = await getDB();
      const all = await db.getAll(STORE_RESPONSES);
      if (!all.length) return null;
      return Math.max(...all.map(e => e.cachedAt));
    } catch (e) {
      return null;
    }
  },

  async clearAll() {
    try {
      const db = await getDB();
      await db.clear(STORE_RESPONSES);
    } catch (e) {
      // Silently swallow
    }
  },
};

export default OfflineCache;
